import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { Button } from '@neo/primitives/button'
import { Label } from '@neo/primitives/label'
import { z } from 'zod'

export interface CredentialFormValues {
  name: string
  username: string
  password: string
}

export const credentialFormFields = (
  isEditing: boolean = false,
  isPasswordEnabled: boolean = false,
  onPasswordChangeClick?: () => void,
): GenericFieldConfig<CredentialFormValues> => [
  {
    name: 'name',
    componentType: 'input',
    label: 'Name',
    inputProps: {
      placeholder: 'Enter credential name',
    },
    className: 'col-span-2',
    eventDataExtractor: (e: React.ChangeEvent<HTMLInputElement>) => e.target.value,
    zodValidation: z
      .string()
      .min(1, 'Name is required')
      .max(100, 'Name must be less than 100 characters'),
  },
  {
    name: 'username',
    componentType: 'input',
    label: 'Username',
    inputProps: {
      placeholder: 'Enter username',
    },
    className: 'col-span-2',
    eventDataExtractor: (e: React.ChangeEvent<HTMLInputElement>) => e.target.value,
    zodValidation: z
      .string()
      .min(1, 'Username is required')
      .min(5, 'Username must be at least 5 characters')
      .max(20, 'Username must be less than 20 characters'),
  },
  {
    name: 'password',
    componentType: 'inputPassword',
    label: isEditing ? (
      <Label className="flex items-center justify-between">
        <span>Password</span>
        {!isPasswordEnabled && (
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              onPasswordChangeClick?.()
            }}
            variant="link"
            size="sm"
            className="h-auto p-0"
            data-testid="button-change-credential-password"
          >
            Change Password
          </Button>
        )}
      </Label>
    ) : (
      // No tooltip: the inline validation messages already spell out the format
      // rules (Tejal, 2026-08-03)
      'Password'
    ),
    inputProps: {
      placeholder: isEditing
        ? isPasswordEnabled
          ? 'Enter new password'
          : '••••••••'
        : 'Enter password',
      disabled: isEditing && !isPasswordEnabled,
    },
    // Copy button rides next to the built-in eye toggle (PasswordInput renders
    // endIcon beside it) so the user can grab the value for their platform
    // config before saving makes it unviewable.
    getDynamicProps: (values) => ({
      inputProps: {
        endIconClassName: 'pr-2',
        endIcon:
          values.password && (!isEditing || isPasswordEnabled) ? (
            <ClipboardCopyButton
              textToCopy={values.password}
              tooltipText="Copy password"
              size="icon"
              variant="ghost"
              className="h-4 w-4"
              data-testid="button-copy-password"
            />
          ) : undefined,
      },
    }),
    className: 'col-span-2',
    eventDataExtractor: (e: React.ChangeEvent<HTMLInputElement>) => e.target.value,
    zodValidation: (() => {
      if (isEditing && !isPasswordEnabled) {
        return z.string().optional()
      }
      const validationRefine = (schema: z.ZodString) =>
        schema
          .min(5, 'Password must be at least 5 characters')
          .max(20, 'Password must be less than 20 characters')
          .refine(
            (val) => /^[a-zA-Z0-9~!@#$%^&*()_+]+$/.test(val),
            'Password must contain only alphanumeric characters and special characters (~!@#$%^&*()_+)',
          )
          .refine(
            (val) => /[~!@#$%^&*()_+]/.test(val),
            'Password must contain at least one special character (~!@#$%^&*()_+)',
          )
      return isEditing
        ? validationRefine(z.string()).optional()
        : validationRefine(z.string().min(1, 'Password is required'))
    })(),
  },
  {
    // Visible banner, deliberately NOT a hover tooltip: the customer must paste
    // this exact username/password into their SIP platform (LiveKit, Retell,
    // ...) and the API never returns the password after saving.
    name: 'password_usage_note',
    type: 'basic',
    component: () => {
      // only when the password input is actually editable: on create, or in
      // edit mode after Change Password (the new value is also view-once)
      if (isEditing && !isPasswordEnabled) return null
      return (
        <div className="col-span-2" data-testid="alert-password-usage-note">
          <Alert
            variant="default"
            content="This password is only shown once. Copy and save it now. You'll need it to configure your SIP platform."
          />
        </div>
      )
    },
  },
]
